import React, { useEffect, useMemo, useState } from "react";

import {
  EnvironmentOutlined,
  MailOutlined,
  PhoneOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Select, Spin } from "antd";

import {
  getSearchableSelectProps,
  renderSelectPopupScrollbar,
  selectPopupClassNames,
} from "../../Common/UIComponents/SelectPopupScrollbar";
import {
  formatAddressLine,
  getAddressId,
  getAddressOptionLabel,
} from "../SupplyPickupTabGroup/Supply/DeliveryDetailDrawer";

import "./AddressDetails.css";

export default function AddressDetails({
  addresses = [],
  selectedAddress = null,
  loading = false,
  onSave,
  title = "Address Details",
  className = "",
  selectId = "default-address",
}) {
  const [selectedId, setSelectedId] = useState(null);

  const addressOptions = useMemo(
    () => (Array.isArray(addresses) ? addresses.filter(Boolean) : []),
    [addresses],
  );

  useEffect(() => {
    if (addressOptions.length === 0) {
      setSelectedId(null);
      return;
    }
    const matchIndex = addressOptions.findIndex((address, index) => (
      getAddressId(address, index) === getAddressId(selectedAddress)
      || (
        address?.name === selectedAddress?.name
        && address?.address_1 === selectedAddress?.address_1
        && address?.zipCode === selectedAddress?.zipCode
      )
    ));
    const resolvedIndex = matchIndex >= 0 ? matchIndex : 0;
    setSelectedId(getAddressId(addressOptions[resolvedIndex], resolvedIndex));
  }, [addressOptions, selectedAddress]);

  const displayAddress = selectedAddress
    || addressOptions.find((address) => getAddressId(address) === selectedId)
    || null;

  const selectOptions = useMemo(
    () => addressOptions.map((address, index) => ({
      value: getAddressId(address, index),
      label: getAddressOptionLabel(address),
      address,
    })),
    [addressOptions],
  );

  const detailItems = [
    { icon: UserOutlined, label: "Contact Name", value: displayAddress?.name || "-" },
    { icon: MailOutlined, label: "Email Address", value: displayAddress?.email || "-" },
    { icon: PhoneOutlined, label: "Phone Number", value: displayAddress?.phone || "-" },
    { icon: EnvironmentOutlined, label: "Address", value: formatAddressLine(displayAddress, "-") },
  ];

  const handleSelectAddress = (value) => {
    setSelectedId(value);
    const match = addressOptions.find((address, index) => getAddressId(address, index) === value);
    if (match && onSave) {
      onSave(match);
    }
  };

  return (
    <div className={`AddressDetails${className ? ` ${className}` : ""}`}>
      <div className="AddressDetails__Header">
        <h2 className="AddressDetails__Title">{title}</h2>
      </div>

      <Spin spinning={loading}>
        <div className="AddressDetails__Body">
          <div className="AddressDetails__SavedSelect">
            <span className="AddressDetails__DetailLabel">Default Address</span>
            <Select
              id={selectId}
              className="AddressDetails__Select"
              {...getSearchableSelectProps()} // eslint-disable-line react/jsx-props-no-spreading
              classNames={selectPopupClassNames("AddressDetails__Dropdown")}
              virtual={false}
              listHeight={256}
              popupRender={(menu) => renderSelectPopupScrollbar(menu, {
                optionCount: (selectOptions || []).length,
              })}
              value={selectedId || undefined}
              onChange={handleSelectAddress}
              options={selectOptions}
              optionLabelProp="label"
              placeholder="-"
              disabled={loading || selectOptions.length === 0}
            />
          </div>

          <div className="AddressDetails__Details">
            {detailItems.map(({ icon: Icon, label, value }) => (
              <div key={label} className="AddressDetails__DetailItem">
                <Icon className="AddressDetails__DetailIcon" aria-hidden />
                <div className="AddressDetails__DetailCopy">
                  <span className="AddressDetails__DetailLabel">{label}</span>
                  <span className="AddressDetails__DetailValue">{value}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Spin>
    </div>
  );
}
